import React, { useState, forwardRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Label from './Label';
import Typography from '@/components/ui/Typography';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import { createLogger } from '@/utils/logger/devLogger';

const modalDateLogger = createLogger('ModalDatePicker');

interface ModalDatePickerProps {
  label?: string;
  value: Date;
  onChange: (date: Date | null) => void;
  showTimeSelect?: boolean;
  showTimeSelectOnly?: boolean;
  required?: boolean;
  error?: string;
  placeholder?: string;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  id?: string;
  name?: string;
  className?: string;
  showIcon?: boolean;
  disabled?: boolean;
  // TailAdmin-style backdrop props
  backdrop?: boolean | 'blur' | 'solid' | 'none';
  backdropOpacity?: 'light' | 'medium' | 'dark';
}

export const ModalDatePicker: React.FC<ModalDatePickerProps> = ({
  label,
  value,
  onChange,
  showTimeSelect = false,
  showTimeSelectOnly = false,
  required = false,
  error,
  placeholder,
  dateFormat = showTimeSelectOnly ? 'h:mm aa' : showTimeSelect ? 'MM/dd/yyyy h:mm aa' : 'MM/dd/yyyy',
  minDate,
  maxDate,
  id,
  name,
  className = '',
  showIcon = true,
  disabled = false,
  backdrop = 'blur',
  backdropOpacity = 'medium',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dateValue = value ? new Date(value) : null;

  // Generate TailAdmin backdrop classes
  const getBackdropClasses = (): string => {
    if (backdrop === 'none' || backdrop === false) {
      return 'hidden';
    }
    
    let classes = 'fixed inset-0 h-full w-full';
    
    // TailAdmin blur effect
    if (backdrop === 'blur' || backdrop === true) {
      classes += ' backdrop-blur-[32px]';
    }
    
    // TailAdmin opacity levels
    switch (backdropOpacity) {
      case 'light':
        classes += ' bg-gray-400/25';
        break;
      case 'medium':
        classes += ' bg-gray-400/50';
        break;
      case 'dark':
        classes += ' bg-gray-400/75';
        break;
      default:
        classes += ' bg-gray-400/50';
    }
    
    return classes;
  };

  // Handle backdrop click
  const handleBackdropClick = () => {
    modalDateLogger.info('Date picker closed via backdrop click');
    setIsOpen(false);
  };

  // Handle date selection
  const handleDateChange = (date: Date | null) => {
    modalDateLogger.info('Date selected:', { date });
    onChange(date);
    setIsOpen(false); // Close after selection
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        modalDateLogger.info('Date picker closed via escape key');
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent scroll
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Custom input component
  const CustomInput = forwardRef<HTMLDivElement, { value?: string; onClick?: () => void }>(
    ({ value, onClick }, ref) => (
      <div 
        className={`
          flex items-center h-11 px-4 py-2.5 w-full rounded-lg border border-gray-300 
          dark:border-gray-700 dark:bg-gray-900 
          ${error ? 'border-red-300 dark:border-red-700' : 'focus-within:border-brand-500 dark:focus-within:border-brand-500'} 
          ${disabled ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : 'cursor-pointer bg-white dark:bg-gray-900'} 
          ${className}
        `}
        onClick={disabled ? undefined : onClick}
        ref={ref}
      >
        {showIcon && (
          <span className="mr-2 text-gray-400">
            {showTimeSelectOnly ? 
              <GoogleMaterialIcon name="access_time"  /> : 
              <GoogleMaterialIcon name="calendar_today"  />
            }
          </span>
        )}
        <Typography
          variant="body-sm"
          color="primary"
          component="div"
          className="flex-grow"
        >
          {value || placeholder || (showTimeSelectOnly ? 'Select time' : 'Select date')}
        </Typography>
      </div>
    )
  );

  return (
    <>
      <div className="w-full">
        {label && (
          <Label htmlFor={id} className="mb-2">
            {label}
          </Label>
        )}
        
        <CustomInput
          value={dateValue ? dateValue.toLocaleDateString() + (showTimeSelect ? ' ' + dateValue.toLocaleTimeString() : '') : ''}
          onClick={() => !disabled && setIsOpen(true)}
        />
        
        {error && (
          <Typography variant="body-xs" component="div" className="mt-1.5 text-red-500 dark:text-red-400">
            {error}
          </Typography>
        )}
      </div>

      {/* TailAdmin-style Modal */}
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center overflow-y-auto modal z-99999">
          {/* TailAdmin Backdrop */}
          <div
            className={getBackdropClasses()}
            onClick={handleBackdropClick}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4">
            {/* Close Button */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute right-3 top-3 z-999 flex h-9.5 w-9.5 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
            >
              <GoogleMaterialIcon name="close"  />
            </button>

            {/* Date Picker */}
            <div className="p-6">
              <DatePicker
                selected={dateValue}
                onChange={handleDateChange}
                showTimeSelect={showTimeSelect}
                showTimeSelectOnly={showTimeSelectOnly}
                timeIntervals={15}
                timeCaption="Time"
                dateFormat={dateFormat}
                minDate={minDate}
                maxDate={maxDate}
                inline // Always show calendar inline in modal
                className="w-full"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModalDatePicker;